import React, { useEffect, useMemo, useRef, useState } from "react";

import { IEspmPropertyTypeCategoryNode } from "../../../services/zeos-api/contracts/EspmPropertyType";
import { ZeosLabel } from "../ZeosLabel";

interface IProps {
  categories: IEspmPropertyTypeCategoryNode[];
  value?: number;
  onChange: (categoryId: number | undefined) => void;
  disabled?: boolean;
  error?: string;
  loading?: boolean;
  required?: boolean;
}

export const EspmPropertyTypeSelect: React.FC<IProps> = ({
  categories,
  value,
  onChange,
  disabled = false,
  error,
  loading = false,
  required = true,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const selected = useMemo(
    () =>
      categories.find((c) => c.espmPropertyTypeCategoryId === value) ??
      undefined,
    [categories, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return categories;
    }
    return categories.filter((c) => c.itemName.toLowerCase().includes(q));
  }, [categories, query]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setActiveIndex(0);
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const selectCategory = (category: IEspmPropertyTypeCategoryNode) => {
    onChange(category.espmPropertyTypeCategoryId);
    setOpen(false);
    setQuery("");
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) {
      return;
    }
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = filtered[activeIndex];
      if (item) {
        selectCategory(item);
      }
    }
  };

  return (
    <div className="w-full" ref={rootRef}>
      <ZeosLabel label="Property Type" required={required} />
      <div className="relative">
        <button
          type="button"
          disabled={disabled || loading}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => !disabled && setOpen((o) => !o)}
          onKeyDown={onTriggerKeyDown}
          className={`
            w-full min-h-[48px] rounded border px-3 py-2 text-left
            bg-white text-[1.125rem] text-zeos-black font-light font-sans
            flex items-center justify-between gap-2
            focus:outline-none focus:ring-2 focus:ring-zeos-blue
            disabled:opacity-60 disabled:cursor-not-allowed
            ${error ? "border-red-500" : "border-[#929293]"}
            ${open ? "ring-2 ring-zeos-blue" : ""}
          `}
        >
          <span className={selected ? "font-medium" : "text-gray-400"}>
            {loading
              ? "Loading property types…"
              : selected?.itemName ?? "Search or select a property type"}
          </span>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            className={`flex-none text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {open && (
          <div className="absolute z-30 mt-2 w-full rounded-lg border border-[#929293] bg-white shadow-lg overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200">
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                className="text-gray-400 flex-none"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.3-4.3" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onListKeyDown}
                placeholder="Type to filter 18 ESPM categories…"
                autoComplete="off"
                className="w-full border-0 outline-none text-sm py-1 bg-transparent"
              />
            </div>
            <ul role="listbox" className="max-h-[320px] overflow-auto p-1 m-0 list-none">
              {filtered.length === 0 && (
                <li className="px-3 py-4 text-center text-sm text-gray-500">
                  No matching category
                </li>
              )}
              {filtered.map((c, idx) => {
                const badge = c.usesAllocation
                  ? { label: "Allocation", className: "bg-orange-50 text-orange-700" }
                  : c.details?.length
                    ? { label: "Subtype", className: "bg-blue-50 text-zeos-blue" }
                    : null;
                return (
                  <li
                    key={c.espmPropertyTypeCategoryId}
                    role="option"
                    aria-selected={c.espmPropertyTypeCategoryId === value}
                    className={`
                      flex items-center justify-between gap-2 px-3 py-2.5 rounded-md cursor-pointer text-sm
                      ${idx === activeIndex || c.espmPropertyTypeCategoryId === value ? "bg-gray-100" : "hover:bg-gray-50"}
                    `}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => selectCategory(c)}
                  >
                    <span className="flex items-center gap-2">
                      <span className="w-5 text-right text-xs text-gray-400 tabular-nums">
                        {categories.indexOf(c) + 1}
                      </span>
                      <span>{c.itemName}</span>
                    </span>
                    {badge && (
                      <span
                        className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-1.5">
        ESPM top-level category. Some categories ask for a subtype to sharpen
        incentive matching.
      </p>
      {error && <div className="text-orange-500 text-xs mt-1">{error}</div>}
    </div>
  );
};
